// ==============================|| INSURANCE PRICING (CLIENT-SIDE PICK) ||============================== //
//
// Backend contract (see `/pharmacy/medicines/`, `/pharmacy/non-medicine-items/`, `/lab/lab-tests/`):
//   Every catalog item ships an optional JSON `pricing` field. The backend
//   exposes the canonical key on each insurer as `pricing_key`.
//   Backend currently returns this object AS-IS regardless of `patient_id`.
//   To honour an insured patient's negotiated rate at billing time, the
//   frontend picks `pricing[insurerKey]`, then the backend-provided hospital
//   price, and uses `pricing.default` only when neither is available.
//
export const getInsurerPricingKey = (activeInsurance) => {
    if (!activeInsurance) return null;
    return activeInsurance.pricing_key ||
        activeInsurance.insurer_details?.pricing_key ||
        activeInsurance.insurer?.pricing_key ||
        null;
};

export const resolvePricingValue = (value, hospitalCode = null) => {
    if (value == null || value === '') return null;
    if (typeof value === 'object' && !Array.isArray(value)) {
        const hospitalValue = hospitalCode
            ? resolvePricingValue(value[hospitalCode], hospitalCode)
            : null;
        if (hospitalValue != null) return hospitalValue;
        const defaultValue = resolvePricingValue(value.default, hospitalCode);
        if (defaultValue != null) return defaultValue;
        for (const candidate of Object.values(value)) {
            const resolved = resolvePricingValue(candidate, hospitalCode);
            if (resolved != null) return resolved;
        }
        return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

export const getEffectiveItemPrice = (item, insurerPricingKey) => {
    if (!item) return null;
    const pricing = item.pricing || {};
    const insurerPrice = insurerPricingKey ? resolvePricingValue(pricing[insurerPricingKey]) : null;
    if (insurerPrice != null) return insurerPrice;
    const hospitalPrice = item.hospital_code
        ? resolvePricingValue(pricing[item.hospital_code], item.hospital_code)
        : null;
    if (hospitalPrice != null) return hospitalPrice;
    const itemPrice = resolvePricingValue(item.price, item.hospital_code);
    if (itemPrice != null) return itemPrice;
    const defaultPrice = resolvePricingValue(pricing.default);
    if (defaultPrice != null) return defaultPrice;
    const sellingPrice = resolvePricingValue(item.selling_price);
    if (sellingPrice != null) return sellingPrice;
    return null;
};

// Rewrites `price` on each search-result item so downstream invoice-line
// construction (which reads `item.selling_price || item.price`) picks the
// insurer rate. We deliberately do NOT touch batch-level `selling_price`
// because pharmacy invoices allocate stock from batches with their own cost.
export const applyInsurerPricing = (items = [], insurerPricingKey) => {
    if (items && !Array.isArray(items) && Array.isArray(items.results)) {
        return {
            ...items,
            results: applyInsurerPricing(items.results, insurerPricingKey),
        };
    }
    if (!Array.isArray(items) || items.length === 0) return items;
    return items.map((it) => {
        const eff = getEffectiveItemPrice(it, insurerPricingKey);
        if (eff == null || Number.isNaN(eff)) return it;
        return { ...it, price: eff };
    });
};
